'use client';

import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
  onClose?: () => void;
}

const toastVariants = cva(
  'relative flex items-start gap-3 w-full max-w-sm p-4 rounded-lg shadow-lg border-l-4 backdrop-blur-sm',
  {
    variants: {
      variant: {
        success: 'bg-gray-900/95 border-green-500 text-white',
        error: 'bg-gray-900/95 border-red-500 text-white',
        warning: 'bg-gray-900/95 border-yellow-500 text-white',
        info: 'bg-gray-900/95 border-blue-500 text-white',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  }
);

const iconVariants = cva('shrink-0 w-5 h-5', {
  variants: {
    variant: {
      success: 'text-green-500',
      error: 'text-red-500',
      warning: 'text-yellow-500',
      info: 'text-blue-500',
    },
  },
});

interface ToastIconProps {
  variant: ToastVariant;
}

const ToastIcon: React.FC<ToastIconProps> = ({ variant }) => {
  const iconClass = iconVariants({ variant });

  switch (variant) {
    case 'success':
      return <CheckCircle className={iconClass} />;
    case 'error':
      return <XCircle className={iconClass} />;
    case 'warning':
      return <AlertTriangle className={iconClass} />;
    case 'info':
      return <Info className={iconClass} />;
    default:
      return <Info className={iconClass} />;
  }
};

export interface ToastProps extends VariantProps<typeof toastVariants> {
  toast: Toast;
  onClose: (id: string) => void;
  className?: string;
}

export const ToastComponent = React.forwardRef<HTMLDivElement, ToastProps>(
  ({ toast, onClose, className }, ref) => {
    const handleClose = () => {
      if (toast.onClose) {
        toast.onClose();
      }
      onClose(toast.id);
    };

    return (
      <motion.div
        ref={ref}
        layout
        initial={{ opacity: 0, x: 300, scale: 0.8 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: 300, scale: 0.8 }}
        transition={{
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1],
        }}
        className={twMerge(toastVariants({ variant: toast.variant }), className)}
      >
        <ToastIcon variant={toast.variant} />

        <div className="flex-1 pt-0.5">
          <p className="text-sm font-medium leading-snug">{toast.message}</p>
        </div>

        <button
          onClick={handleClose}
          className="shrink-0 rounded p-1 hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white/20"
          aria-label="Close notification"
        >
          <X className="w-4 h-4 text-gray-400 hover:text-white" />
        </button>
      </motion.div>
    );
  }
);

ToastComponent.displayName = 'Toast';

export interface ToastListProps {
  toasts: Toast[];
  onClose: (id: string) => void;
  maxVisible?: number;
}

export const ToastList: React.FC<ToastListProps> = ({
  toasts,
  onClose,
  maxVisible = 5,
}) => {
  const visibleToasts = toasts.slice(0, maxVisible);

  return (
    <div className="fixed top-4 right-4 z-50 pointer-events-none">
      <div className="flex flex-col gap-3 pointer-events-auto">
        <AnimatePresence mode="popLayout">
          {visibleToasts.map((toast) => (
            <ToastComponent key={toast.id} toast={toast} onClose={onClose} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};
